import { useTranslation } from 'react-i18next';
import type {
  GoodsReceiptStatus,
  PurchaseOrderStatus,
  ThreeWayMatchStatus,
  VendorInvoiceStatus,
} from '@erp/shared';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const ORDER_VARIANTS: Readonly<Record<PurchaseOrderStatus, BadgeVariant>> = {
  DRAFT: 'outline',
  SENT: 'secondary',
  CONFIRMED: 'secondary',
  PARTIALLY_RECEIVED: 'warning',
  RECEIVED: 'success',
  BILLED: 'default',
  CANCELLED: 'danger',
};

export function PurchaseOrderStatusBadge({ status }: { status: PurchaseOrderStatus }): JSX.Element {
  const { t } = useTranslation();
  return <Badge variant={ORDER_VARIANTS[status]}>{t(`procurement.orderStatus.${status}`)}</Badge>;
}

const RECEIPT_VARIANTS: Readonly<Record<GoodsReceiptStatus, BadgeVariant>> = {
  DRAFT: 'outline',
  POSTED: 'success',
  CANCELLED: 'danger',
};

export function GoodsReceiptStatusBadge({ status }: { status: GoodsReceiptStatus }): JSX.Element {
  const { t } = useTranslation();
  return (
    <Badge variant={RECEIPT_VARIANTS[status]}>{t(`procurement.receiptStatus.${status}`)}</Badge>
  );
}

const INVOICE_VARIANTS: Readonly<Record<VendorInvoiceStatus, BadgeVariant>> = {
  DRAFT: 'outline',
  MATCHED: 'secondary',
  DISCREPANCY: 'danger',
  APPROVED_FOR_PAYMENT: 'success',
  PAID: 'default',
  CANCELLED: 'danger',
};

export function VendorInvoiceStatusBadge({ status }: { status: VendorInvoiceStatus }): JSX.Element {
  const { t } = useTranslation();
  return (
    <Badge variant={INVOICE_VARIANTS[status]}>{t(`procurement.invoiceStatus.${status}`)}</Badge>
  );
}

/** Ampel des 3-Wege-Abgleichs: grün bei Übereinstimmung, rot bei Abweichung. */
export function MatchStatusLight({ status }: { status: ThreeWayMatchStatus }): JSX.Element {
  const { t } = useTranslation();
  const label = t(`procurement.matchStatus.${status}`);
  const isClear =
    status === 'PERFECT_MATCH' ||
    status === 'TOLERANCE_ACCEPTED' ||
    status === 'MANUALLY_OVERRIDDEN';

  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span
        aria-hidden="true"
        className={cn(
          'h-2.5 w-2.5 shrink-0 rounded-full',
          status === 'PERFECT_MATCH'
            ? 'bg-emerald-500'
            : isClear
              ? 'bg-amber-500'
              : 'bg-destructive',
        )}
      />
      <span className="text-xs">{label}</span>
    </span>
  );
}
